// src/components/TrainingDaysSelector.jsx
"use client";

import React, { useState, useEffect } from "react";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthProvider";
import { collection, addDoc, query, orderBy, limit, getDocs, serverTimestamp } from "firebase/firestore";
import { calculatePaces } from "../lib/paceCalculator";
import { generateTrainingPlan } from "../lib/planGenerator";

// Define the three valid options with explicit block splits.
const validOptions = [
    {
        id: "option1",
        block1: ["Monday", "Tuesday", "Wednesday"],
        block2: ["Friday", "Saturday"],
        ambiguousDays: ["Friday", "Saturday"],
    },
    {
        id: "option2",
        block1: ["Tuesday", "Wednesday", "Thursday"],
        block2: ["Saturday", "Sunday"],
        ambiguousDays: ["Saturday", "Sunday"],
    },
    {
        id: "option3",
        block1: ["Wednesday", "Thursday", "Friday"],
        block2: ["Sunday", "Monday"],
        ambiguousDays: ["Sunday", "Monday"],
    },
];

const TrainingDaysSelector = () => {
    const { user } = useAuth();

    const [selectedDays, setSelectedDays] = useState([]);
    const [error, setError] = useState("");
    const [ambiguousSelections, setAmbiguousSelections] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [latestTest, setLatestTest] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);

    const [planLevel, setPlanLevel] = useState(2); // Default to Level 2
    const [showPlanLevelInfo, setShowPlanLevelInfo] = useState(false);

    // Fetch the latest 6-minute test result from Firestore
    useEffect(() => {
        async function fetchLatestTest() {
            if (!user) return;

            try {
                const testsRef = collection(db, "users", user.uid, "sixMinTests");
                const q = query(testsRef, orderBy("createdAt", "desc"), limit(1));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const doc = querySnapshot.docs[0];
                    const data = doc.data();
                    setLatestTest(data);
                } else {
                    setLatestTest(null);
                }
            } catch (err) {
                console.error("Error fetching latest test:", err?.message || err);
                setLatestTest(null);

                // Provide specific error messages based on error type
                if (err?.code === "permission-denied") {
                    setError("You don't have permission to view tests. Please sign in again.");
                } else if (err?.code === "unavailable") {
                    setError("Unable to connect to the server. Please check your internet connection.");
                } else {
                    setError("Failed to load test data. Please refresh the page.");
                }
            }
        }

        fetchLatestTest();
    }, [user]);

    // Toggle day selection on click
    const toggleDay = (day) => {
        setError("");
        setSubmitSuccess(false);

        if (selectedDays.includes(day)) {
            setSelectedDays(selectedDays.filter((d) => d !== day));
        } else {
            if (selectedDays.length < 5) {
                setSelectedDays([...selectedDays, day]);
            }
        }
    };

    // When exactly 5 days are selected, determine the valid option.
    useEffect(() => {
        setError("");

        if (selectedDays.length === 5) {
            const candidate = validOptions.find((option) => {
                const optionDays = [...option.block1, ...option.block2].sort();
                const selected = [...selectedDays].sort();
                return JSON.stringify(optionDays) === JSON.stringify(selected);
            });

            if (candidate) {
                setSelectedOption(candidate);
            } else {
                setSelectedOption(null);
                setError(
                    "Invalid selection. Please select one of the valid sequences: " +
                    validOptions
                        .map((opt) => [...opt.block1, ...opt.block2].join(", "))
                        .join(" | ")
                );
            }
        } else {
            setSelectedOption(null);
        }
    }, [selectedDays]);

    // Handle change for ambiguous day selection (radio buttons)
    const handleAmbiguousChange = (day, value) => {
        setAmbiguousSelections((prev) => ({
            ...prev,
            [day]: value,
        }));
    };

    // Handle plan level selection change
    const handlePlanLevelChange = (level) => {
        setPlanLevel(level);
    };

    // Handle form submission: Save training days, ambiguous selections, plan level, and plan to Firestore.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitSuccess(false);

        if (selectedDays.length !== 5) {
            setError("Please select exactly 5 days for training.");
            return;
        }

        if (!selectedOption) {
            setError("Selected days do not match any valid option.");
            return;
        }

        for (const day of selectedOption.ambiguousDays) {
            if (!ambiguousSelections[day]) {
                setError(`Please choose between "Easy Run #2" and "Long Run" for ${day}.`);
                return;
            }
        }

        if (
            selectedOption.ambiguousDays.length === 2 &&
            ambiguousSelections[selectedOption.ambiguousDays[0]] ===
            ambiguousSelections[selectedOption.ambiguousDays[1]]
        ) {
            setError(
                "For the ambiguous days, you must select different workouts: one must be 'Easy Run #2' and the other 'Long Run'."
            );
            return;
        }

        if (!latestTest) {
            setError(
                "No 6-minute test result found. Please complete a 6-minute test before saving your training plan."
            );
            return;
        }

        if (!user) {
            setError("You must be logged in to save your training plan.");
            return;
        }

        setSubmitLoading(true);

        try {
            const computedPaces = calculatePaces(
                latestTest.minutes,
                latestTest.seconds,
                latestTest.unit
            );

            const populatedPlan = generateTrainingPlan(
                computedPaces,
                selectedOption,
                ambiguousSelections,
                planLevel
            );

            const planData = {
                days: [...selectedOption.block1, ...selectedOption.block2],
                ambiguousSelections,
                optionId: selectedOption.id,
                planLevel,
                createdAt: serverTimestamp(),
                plan: populatedPlan,
            };

            const plansCollectionRef = collection(db, "users", user.uid, "trainingPlans", "5k", "plans");
            await addDoc(plansCollectionRef, planData);

            setSubmitSuccess(true);
            setSelectedDays([]);
            setAmbiguousSelections({});
        } catch (err) {
            console.error("Error saving training plan:", err?.message || err);

            // Provide specific error messages based on error type
            if (err?.code === "permission-denied") {
                setError("You don't have permission to save the training plan. Please sign in again.");
            } else if (err?.code === "unavailable") {
                setError("Unable to connect to the server. Please check your internet connection.");
            } else {
                setError("Failed to save training plan. Please try again.");
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="p-2 sm:p-2">
            <h2 className="text-2xl font-bold mb-4">Select Training Days</h2>
            <p>Select exactly 5 days by clicking on the day names.</p>

            {/* Responsive day buttons using a grid layout */}
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 my-4">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
                    (day) => (
                        <button
                            key={day}
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-2 border rounded ${selectedDays.includes(day)
                                ? "bg-blue-500 text-white"
                                : "bg-white text-black"
                                }`}
                        >
                            {day.slice(0, 3)}
                        </button>
                    )
                )}
            </div>

            {selectedDays.length > 0 && (
                <p className="text-center">
                    Selected Days:{" "}
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
                        .filter((day) => selectedDays.includes(day))
                        .join(", ")}
                </p>
            )}

            {/* Ambiguous day selection */}
            {selectedOption &&
                selectedOption.ambiguousDays.map((day) => (
                    <div key={day} className="mt-4">
                        <p>{day}:</p>
                        <div className="flex items-center gap-4">
                            <label>
                                <input
                                    type="radio"
                                    name={day}
                                    value="Easy Run #2"
                                    checked={ambiguousSelections[day] === "Easy Run #2"}
                                    onChange={() => handleAmbiguousChange(day, "Easy Run #2")}
                                />{" "}
                                Easy Run #2
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name={day}
                                    value="Long Run"
                                    checked={ambiguousSelections[day] === "Long Run"}
                                    onChange={() => handleAmbiguousChange(day, "Long Run")}
                                />{" "}
                                Long Run
                            </label>
                        </div>
                    </div>
                ))}

            {error && <p className="text-red-500 mt-2 text-center">{error}</p>}

            {/* Plan Level Selection */}
            <div className="mb-4">
                <div className="flex items-center pt-4 mb-4">
                    <h3 className="text-2xl font-bold">Select Plan Level</h3>
                    <span
                        className="ml-2 cursor-pointer text-red-500"
                        onClick={() => setShowPlanLevelInfo(true)}
                    >
                        ?
                    </span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <label>
                        <input
                            type="radio"
                            name="planLevel"
                            value="1"
                            checked={planLevel === 1}
                            onChange={() => handlePlanLevelChange(1)}
                        />{" "}
                        Level 1
                    </label>

                    <label>
                        <input
                            type="radio"
                            name="planLevel"
                            value="2"
                            checked={planLevel === 2}
                            onChange={() => handlePlanLevelChange(2)}
                        />{" "}
                        Level 2
                    </label>

                    <label>
                        <input
                            type="radio"
                            name="planLevel"
                            value="3"
                            checked={planLevel === 3}
                            onChange={() => handlePlanLevelChange(3)}
                        />{" "}
                        Level 3
                    </label>
                </div>
            </div>

            {/* Submit button */}
            <div className="flex mt-4">
                <button
                    onClick={handleSubmit}
                    disabled={submitLoading}
                    className="bg-green-500 text-white px-4 py-2 rounded"
                >
                    {submitLoading ? "Creating..." : "Create Training Plan"}
                </button>
            </div>

            {submitSuccess && (
                <p className="text-green-600 mt-2 text-center">
                    Training plan saved successfully!
                </p>
            )}

            {/* Overlay for Plan Level Info */}
            {showPlanLevelInfo && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
                    onClick={() => setShowPlanLevelInfo(false)}
                >
                    <div
                        className="bg-white p-4 rounded-md relative max-w-md w-full max-h-[80vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowPlanLevelInfo(false)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                        >
                            X
                        </button>

                        <h2 className="text-xl font-bold mb-2 text-center">
                            Plan Level Information
                        </h2>

                        <div className="space-y-4">
                            <p>
                                <strong>Level 1:</strong> Select this plan if your 5k goal is 25
                                minutes or slower.
                            </p>
                            <p>
                                <strong>Level 2:</strong> Select this plan if your 5k goal is
                                between 20 and 25 minutes.
                            </p>
                            <p>
                                <strong>Level 3:</strong> Select this plan if your goal is below
                                20 minutes.
                            </p>
                            <p className="text-sm text-center">
                                You can always change the level in the future. But your plan
                                will start from day 1.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingDaysSelector;
